const API_BASE = import.meta.env.VITE_API_BASE_URL;

export type CheckoutNetwork =
  | 'ETHEREUM'
  | 'BASE'
  | 'ARBITRUM'
  | 'OPTIMISM'
  | 'POLYGON'
  | 'SOLANA'
  | 'BITCOIN'
  | 'TRON';

export type CheckoutStatus =
  | 'OPEN'
  | 'SESSION_ACTIVE'
  | 'PAID'
  | 'OVERPAID'
  | 'UNDERPAID_REFUNDED'
  | 'EXPIRED'
  | 'CANCELED'
  | 'FLAGGED_PENDING_REVIEW'
  | 'FLAGGED_PLATFORM_HELD'
  | 'FLAGGED_AUTO_RETURNED'
  | 'RISK_SCAN_PENDING';

export type CheckoutSessionStatus =
  | 'AWAITING_PAYMENT'
  | 'PARTIAL'
  | 'CONFIRMING'
  | 'PAID'
  | 'OVERPAID'
  | 'EXPIRED'
  | 'REFUNDING'
  | 'REFUNDED'
  | 'REFUND_FAILED'
  | 'FLAGGED_PENDING_REVIEW'
  | 'FLAGGED_PLATFORM_HELD'
  | 'RISK_SCAN_PENDING';

export const CHECKOUT_STATUS_MESSAGES: Record<CheckoutStatus, string> = {
  OPEN: 'Waiting for you to start payment.',
  SESSION_ACTIVE: 'Payment session is active.',
  PAID: 'Payment received.',
  OVERPAID: 'Payment received with extra amount.',
  UNDERPAID_REFUNDED: 'Underpaid amount was refunded.',
  EXPIRED: 'This payment link has expired.',
  CANCELED: 'This payment link is no longer active.',
  FLAGGED_PENDING_REVIEW: 'Payment is under review.',
  FLAGGED_PLATFORM_HELD: 'Payment is under review by our team.',
  FLAGGED_AUTO_RETURNED: 'Payment was automatically returned.',
  RISK_SCAN_PENDING: 'Payment is being verified.',
};

export const CHECKOUT_SESSION_STATUS_MESSAGES: Record<CheckoutSessionStatus, string> = {
  AWAITING_PAYMENT: 'Waiting for payment...',
  PARTIAL: 'Partial payment received. Waiting for the remainder...',
  CONFIRMING: 'Payment detected, confirming...',
  PAID: 'Payment received.',
  OVERPAID: 'Payment received with extra amount.',
  EXPIRED: 'Payment window expired.',
  REFUNDING: 'Refund in progress.',
  REFUNDED: 'Refund sent.',
  REFUND_FAILED: 'Refund failed. Please contact support.',
  FLAGGED_PENDING_REVIEW: 'Payment is under review.',
  FLAGGED_PLATFORM_HELD: 'Payment is under review by our team.',
  RISK_SCAN_PENDING: 'Payment is being verified.',
};

export function isCheckoutTerminalStatus(status: CheckoutStatus): boolean {
  return status !== 'OPEN' && status !== 'SESSION_ACTIVE' && status !== 'RISK_SCAN_PENDING';
}

export function isCheckoutSessionTerminalStatus(status: CheckoutSessionStatus): boolean {
  return (
    status !== 'AWAITING_PAYMENT' &&
    status !== 'PARTIAL' &&
    status !== 'CONFIRMING' &&
    status !== 'RISK_SCAN_PENDING' &&
    status !== 'REFUNDING'
  );
}

export interface NetworkTokenPair {
  network: CheckoutNetwork;
  token: string;
  mintId?: number;
  image?: string | null;
}

export interface AvailableMint {
  chain: CheckoutNetwork;
  token: string;
  isTestnet: boolean;
  mintId?: number;
  image?: string | null;
}

export function mintKey(mint: AvailableMint): string {
  return `${mint.chain}:${mint.token}:${mint.isTestnet ? 'testnet' : 'mainnet'}`;
}

export function chainTokenPairKey(chain: string, token: string): string {
  return `${chain}:${token}`;
}

type AcceptedPairLike = {
  network?: string;
  chain?: string;
  token: string;
  mintId?: number;
};

export function acceptedPairKeyFromSnapshot(pair: AcceptedPairLike): string | null {
  const chain = pair.network ?? pair.chain;
  if (!chain) return null;
  return chainTokenPairKey(chain, pair.token);
}

export function filterMintsForCheckout(
  allMints: AvailableMint[],
  acceptedPairs: AcceptedPairLike[],
): AvailableMint[] {
  if (acceptedPairs.length === 0) return allMints;

  const pairKeys = new Set<string>();
  const snapshotMintIds = new Set<number>();
  for (const pair of acceptedPairs) {
    const key = acceptedPairKeyFromSnapshot(pair);
    if (key) pairKeys.add(key);
    if (pair.mintId != null) snapshotMintIds.add(pair.mintId);
  }

  return allMints.filter(
    (mint) =>
      pairKeys.has(chainTokenPairKey(mint.chain, mint.token)) ||
      (mint.mintId != null && snapshotMintIds.has(mint.mintId)),
  );
}

/**
 * An asset the payer can pay with that Cheddar does not custody directly.
 * Provider catalogue fields only — the payer sees the symbol and the chain and
 * nothing else, exactly like a native mint.
 */
export interface SwapToken {
  assetId: string;
  blockchain: string;
  symbol: string;
  decimals: number;
  contractAddress: string | null;
}

export interface ProductSnapshot {
  name?: string;
  description?: string;
  imageUrl?: string | null;
}

export interface CheckoutLineItemResponse {
  id: string;
  productId: string;
  quantity: number;
  unitPriceUsdSnapshot: string;
  lineTotalUsdSnapshot: string;
  productSnapshot: ProductSnapshot & { sku?: string };
  sortOrder: number;
}

export interface CheckoutResponse {
  id: string;
  status: CheckoutStatus;
  priceUsdSnapshot: string;
  lineItems: CheckoutLineItemResponse[];
  acceptedNetworksTokensSnapshot: NetworkTokenPair[];
  expiresAt: string;
  hostedUrl: string;
  successUrl: string | null;
  cancelUrl: string | null;
}

/**
 * The leg of the payment the payer actually performs when they pay with a
 * token Cheddar does not custody: where to send it, how much, and the memo
 * without which the funds are lost.
 *
 * `null` on an ordinary session, in which case nothing about this exists.
 */
export interface CheckoutSessionSwap {
  depositAddress: string;
  /** Required alongside the address where present — omitting it loses funds. */
  depositMemo: string | null;
  /**
   * The amount to send, in human units of the token the payer is sending.
   * `null` when the server could not resolve it; never substitute a number.
   */
  maxAmountIn: string | null;
  originSymbol: string;
  originBlockchain: string;
  deadline: string;
  status: string;
  /** Stable key for terminal-failure copy, or `null`. */
  failureCopyKey: string | null;
}

export interface CheckoutSessionResponse {
  id: string;
  checkoutId: string;
  network: CheckoutNetwork;
  token: string;
  rateLocked: string;
  amountRequired: string;
  amountReceived: string;
  depositAddress: string;
  refundAddress: string;
  minConfirmations: number;
  expiresAt: string;
  status: CheckoutSessionStatus;
  createdAt: string;
  updatedAt: string;
  swap: CheckoutSessionSwap | null;
}

type RawCheckoutSessionResponse = Omit<
  CheckoutSessionResponse,
  'network' | 'swap'
> & {
  network?: CheckoutNetwork;
  chain?: CheckoutNetwork;
  swap?: unknown;
};

function assertApiBase() {
  if (!API_BASE) {
    throw new Error('VITE_API_BASE_URL is not set');
  }
}

function isCheckoutNetwork(value: unknown): value is CheckoutNetwork {
  return (
    value === 'ETHEREUM' ||
    value === 'BASE' ||
    value === 'ARBITRUM' ||
    value === 'OPTIMISM' ||
    value === 'POLYGON' ||
    value === 'SOLANA' ||
    value === 'BITCOIN' ||
    value === 'TRON'
  );
}

function normalizeNetworkTokenPairs(value: unknown): NetworkTokenPair[] {
  const parsed =
    typeof value === 'string'
      ? (() => {
          try {
            return JSON.parse(value) as unknown;
          } catch {
            return [];
          }
        })()
      : value;
  if (!Array.isArray(parsed)) return [];
  return parsed.flatMap((entry) => {
    if (typeof entry !== 'object' || entry == null) return [];
    const token = 'token' in entry && typeof entry.token === 'string' ? entry.token : null;
    const mintId =
      'mintId' in entry && typeof entry.mintId === 'number' ? entry.mintId : undefined;
    const image =
      'image' in entry && (typeof entry.image === 'string' || entry.image === null)
        ? entry.image
        : undefined;
    const candidateNetwork =
      'network' in entry && typeof entry.network === 'string'
        ? entry.network
        : 'chain' in entry && typeof entry.chain === 'string'
          ? entry.chain
          : null;
    if (!token || !candidateNetwork || !isCheckoutNetwork(candidateNetwork)) {
      return [];
    }
    return [{ network: candidateNetwork, token, mintId, image }];
  });
}

function normalizeProductSnapshot(value: unknown): ProductSnapshot {
  const parsed =
    typeof value === 'string'
      ? (() => {
          try {
            return JSON.parse(value) as unknown;
          } catch {
            return {};
          }
        })()
      : value;
  if (typeof parsed !== 'object' || parsed == null) return {};
  return {
    name: 'name' in parsed && typeof parsed.name === 'string' ? parsed.name : undefined,
    description:
      'description' in parsed && typeof parsed.description === 'string'
        ? parsed.description
        : undefined,
    imageUrl:
      'imageUrl' in parsed &&
      (typeof parsed.imageUrl === 'string' || parsed.imageUrl === null)
        ? parsed.imageUrl
        : undefined,
  };
}

function normalizeLineItem(value: unknown): CheckoutLineItemResponse | null {
  if (typeof value !== 'object' || value == null) return null;
  const raw = value as Record<string, unknown>;
  const quantity = typeof raw.quantity === 'number' ? raw.quantity : null;
  const productId = typeof raw.productId === 'string' ? raw.productId : null;
  const id = typeof raw.id === 'string' ? raw.id : null;
  const unitPriceUsdSnapshot =
    typeof raw.unitPriceUsdSnapshot === 'string' ? raw.unitPriceUsdSnapshot : null;
  const lineTotalUsdSnapshot =
    typeof raw.lineTotalUsdSnapshot === 'string' ? raw.lineTotalUsdSnapshot : null;
  const sortOrder = typeof raw.sortOrder === 'number' ? raw.sortOrder : 0;
  if (
    quantity == null ||
    productId == null ||
    id == null ||
    unitPriceUsdSnapshot == null ||
    lineTotalUsdSnapshot == null
  ) {
    return null;
  }
  return {
    id,
    productId,
    quantity,
    unitPriceUsdSnapshot,
    lineTotalUsdSnapshot,
    productSnapshot: normalizeProductSnapshot(raw.productSnapshot),
    sortOrder,
  };
}

function normalizeCheckoutResponse(value: unknown): CheckoutResponse {
  const raw = value as CheckoutResponse & {
    acceptedNetworksTokensSnapshot?: unknown;
    lineItems?: unknown;
  };
  const lineItems = Array.isArray(raw.lineItems)
    ? raw.lineItems.flatMap((entry) => {
        const line = normalizeLineItem(entry);
        return line ? [line] : [];
      })
    : [];
  return {
    ...raw,
    lineItems,
    acceptedNetworksTokensSnapshot: normalizeNetworkTokenPairs(
      raw.acceptedNetworksTokensSnapshot,
    ),
  };
}

function normalizeAvailableMint(value: unknown): AvailableMint | null {
  if (typeof value !== 'object' || value == null) return null;
  const chain =
    ('chain' in value && typeof value.chain === 'string' ? value.chain : null) ??
    ('network' in value && typeof value.network === 'string' ? value.network : null);
  const token =
    'token' in value && typeof value.token === 'string' ? value.token : null;
  const isTestnet = 'isTestnet' in value && typeof value.isTestnet === 'boolean'
    ? value.isTestnet
    : false;
  if (!chain || !token || !isCheckoutNetwork(chain)) return null;
  const mintId =
    'mintId' in value && typeof value.mintId === 'number' ? value.mintId : undefined;
  const image =
    'image' in value && (typeof value.image === 'string' || value.image === null)
      ? value.image
      : undefined;
  return { chain, token, isTestnet, mintId, image };
}

function normalizeSwapToken(value: unknown): SwapToken | null {
  if (typeof value !== 'object' || value == null) return null;
  const raw = value as Record<string, unknown>;
  const assetId = typeof raw.assetId === 'string' ? raw.assetId : null;
  const blockchain = typeof raw.blockchain === 'string' ? raw.blockchain : null;
  const symbol = typeof raw.symbol === 'string' ? raw.symbol : null;
  const decimals = typeof raw.decimals === 'number' ? raw.decimals : null;
  if (!assetId || !blockchain || !symbol || decimals == null) return null;
  return {
    assetId,
    blockchain,
    symbol,
    decimals,
    contractAddress:
      typeof raw.contractAddress === 'string' ? raw.contractAddress : null,
  };
}

function normalizeSessionSwap(value: unknown): CheckoutSessionSwap | null {
  if (typeof value !== 'object' || value == null) return null;
  const raw = value as Record<string, unknown>;
  const depositAddress =
    typeof raw.depositAddress === 'string' ? raw.depositAddress : null;
  const originSymbol =
    typeof raw.originSymbol === 'string' ? raw.originSymbol : null;
  const originBlockchain =
    typeof raw.originBlockchain === 'string' ? raw.originBlockchain : null;
  // Without an address there is nothing the payer can act on, so a malformed
  // block is dropped rather than half-rendered.
  if (!depositAddress || !originSymbol || !originBlockchain) return null;
  return {
    depositAddress,
    depositMemo: typeof raw.depositMemo === 'string' ? raw.depositMemo : null,
    // Stays null rather than becoming 0 — a fabricated amount is worse than
    // no amount, because the payer cannot top up a short send.
    maxAmountIn: typeof raw.maxAmountIn === 'string' ? raw.maxAmountIn : null,
    originSymbol,
    originBlockchain,
    deadline: typeof raw.deadline === 'string' ? raw.deadline : '',
    status: typeof raw.status === 'string' ? raw.status : '',
    failureCopyKey:
      typeof raw.failureCopyKey === 'string' ? raw.failureCopyKey : null,
  };
}

function normalizeCheckoutSessionResponse(value: unknown): CheckoutSessionResponse {
  const raw = value as RawCheckoutSessionResponse;
  const network = raw.network ?? raw.chain;
  if (!network || !isCheckoutNetwork(network)) {
    throw new Error('Invalid checkout session network');
  }
  return {
    ...raw,
    network,
    swap: normalizeSessionSwap(raw.swap),
  };
}

export async function getCheckout(checkoutId: string): Promise<CheckoutResponse> {
  assertApiBase();
  const res = await fetch(`${API_BASE}/v1/public/checkouts/${checkoutId}`);
  if (!res.ok) throw new Error('Checkout not found');
  const data = (await res.json()) as unknown;
  return normalizeCheckoutResponse(data);
}

export async function getActiveSession(
  checkoutId: string,
): Promise<CheckoutSessionResponse | null> {
  assertApiBase();
  const res = await fetch(`${API_BASE}/v1/public/checkouts/${checkoutId}/active-session`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error('Could not load session');
  const data = (await res.json()) as unknown;
  return normalizeCheckoutSessionResponse(data);
}

export type CreateSessionResult =
  | { conflict: false; session: CheckoutSessionResponse }
  | { conflict: true; activeSessionId: string };

/**
 * `mintId` for a mint Cheddar custodies; `origin_asset_id` + `refund_address`
 * for one it does not. Never both — the server forces the session mint on the
 * second branch and rejects one of the two extra fields without the other.
 */
export type CreateSessionPayload =
  | { mintId: number | undefined }
  | { origin_asset_id: string; refund_address: string };

export async function createSession(
  checkoutId: string,
  payload: CreateSessionPayload,
): Promise<CreateSessionResult> {
  assertApiBase();
  const res = await fetch(`${API_BASE}/v1/public/checkouts/${checkoutId}/sessions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (res.status === 409) {
    const data = (await res.json()) as { activeSessionId?: string; error?: string };
    if (data.activeSessionId) {
      return { conflict: true, activeSessionId: data.activeSessionId };
    }
    throw new Error(data.error ?? 'Session conflict');
  }
  if (!res.ok) {
    // The body is the `{ error: <slug> }` envelope; the slug is stable and is
    // what the caller maps to copy. Never render it directly.
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(body?.error || 'could_not_start_payment');
  }
  const data = (await res.json()) as unknown;
  const session = normalizeCheckoutSessionResponse(data);
  return { conflict: false, session };
}

export async function getSession(
  checkoutId: string,
  sessionId: string,
): Promise<CheckoutSessionResponse> {
  assertApiBase();
  const res = await fetch(
    `${API_BASE}/v1/public/checkouts/${checkoutId}/sessions/${sessionId}`,
  );
  if (!res.ok) throw new Error('Session not found');
  const data = (await res.json()) as unknown;
  return normalizeCheckoutSessionResponse(data);
}

export async function getAvailableMints(): Promise<AvailableMint[]> {
  assertApiBase();
  const res = await fetch(`${API_BASE}/v1/public/mints`);
  if (!res.ok) throw new Error('Could not load payment methods');
  const data = (await res.json()) as unknown;
  if (!Array.isArray(data)) return [];
  return data.flatMap((entry) => {
    const mint = normalizeAvailableMint(entry);
    return mint ? [mint] : [];
  });
}

/**
 * Extra tokens this checkout can be paid with, or `[]`.
 *
 * **Never throws and never distinguishes "not offered" from "could not ask".**
 * The endpoint returns an empty list when the option is unavailable, and any
 * transport or parsing failure has to look the same, so an outage can never
 * change what the payer is shown.
 */
export async function getSwapTokens(checkoutId: string): Promise<SwapToken[]> {
  try {
    assertApiBase();
    const params = new URLSearchParams({ checkoutId });
    const res = await fetch(`${API_BASE}/v1/public/swap-tokens?${params.toString()}`);
    if (!res.ok) return [];
    const data = (await res.json()) as unknown;
    if (typeof data !== 'object' || data == null || !('tokens' in data)) return [];
    const tokens = (data as { tokens: unknown }).tokens;
    if (!Array.isArray(tokens)) return [];
    return tokens.flatMap((entry) => {
      const token = normalizeSwapToken(entry);
      return token ? [token] : [];
    });
  } catch {
    return [];
  }
}

export async function getIndicativeQuote(mintId: number): Promise<number> {
  assertApiBase();
  const params = new URLSearchParams({ mintId: String(mintId) });
  const res = await fetch(`${API_BASE}/v1/public/quote?${params.toString()}`);
  if (!res.ok) throw new Error('quote_unavailable');
  const data = (await res.json()) as { usdPrice: number };
  return data.usdPrice;
}
